/**
 * Feedback trigger plus panel, ported from Prism's `FeedbackWidget` onto the
 * dashboard's Macondo shim and AuthContext. Mounted once in the top bar with
 * `variant="docked"`; the default `floating` pill is kept for surfaces with no
 * top bar.
 *
 * The verdict badge is ADVISORY. `disabled` on Send depends only on the text
 * being non-empty and a submission not being in flight; a bad verdict never
 * blocks anything and a failed evaluation renders nothing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button, IconButton, Modal, Spinner, Textarea, Toggle, Tooltip, TooltipContent, TooltipTrigger, toast,
} from '@/lib/macondo';
import { useAuth } from '@/auth/AuthContext';
import { withBase } from '@/lib/basePath';
import { MESSAGES } from './strings';
import { getConsoleErrors } from './console-buffer';
import type { ConsoleEntry } from './redact';
import { captureScreenshot } from './screenshot';
import { getSessionSeconds } from './session';
import { submitFeedback, FeedbackSubmitError } from './submit';
import type { FeedbackTool } from './types';
import { useFeedbackVerdict } from './useVerdict';
import type { Verdict } from './evaluate';

// Inline SVG (message-square), never an emoji - per design, ported verbatim.
const TRIGGER_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/**
 * `2m 3s`. NOT extracted: `m` and `s` are unit symbols, which
 * docs/design/i18n-plan.md §4.5 lists as not-copy, and the digits are ASCII
 * integers with no grouping or decimals for `Intl.NumberFormat` to change.
 * This value is also a diagnostic field on a feedback ticket read by us, not
 * a sentence read by the operator.
 */
function formatSessionLength(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/**
 * The verdict's three tones, as semantic token pairs.
 *
 * Links styles these in `FeedbackWidget.css` with the same three token
 * families (`--success-*`, `--info-*`, `--critical-*`) - Prism reaches them
 * through macondo's utilities instead of hand-written CSS, same values. The
 * mapping is one-to-one with the panel colours the same verdict gets on the
 * Jira ticket afterwards (good → green, neutral → blue, bad → red), so the
 * badge somebody sees before submitting matches the ticket it becomes.
 *
 * The LABEL is what actually carries the verdict - colour is never the only
 * signal, which is why each row keeps its uppercase text.
 */
const VERDICT_TONES: Record<Verdict, string> = {
  good: 'bg-success-surface text-success-text',
  neutral: 'bg-info-surface text-info-text',
  bad: 'bg-critical-surface text-critical-text',
};

/**
 * i18n keys for the three labels. Extracted like any other operator-facing
 * copy (§4.1) even though the English is the same wording leon-respond puts
 * on the ticket: the badge is read by the operator, the ticket panel is read
 * by us.
 */
const VERDICT_LABEL_KEYS = {
  good: 'verdictGood',
  neutral: 'verdictNeutral',
  bad: 'verdictBad',
} as const;

/** Which trigger to draw - see the header. The modal is the same either way. */
export type FeedbackWidgetVariant = 'floating' | 'docked';

export interface FeedbackWidgetProps {
  /** The active section's id, or `'shell'` for chrome-level feedback - see ./types.ts. */
  tool: FeedbackTool;
  /** `'docked'` renders the topbar's 32px icon button; the default keeps the corner pill. */
  variant?: FeedbackWidgetVariant;
}

export function FeedbackWidget({ tool, variant = 'floating' }: FeedbackWidgetProps) {
  const { user, csrfToken } = useAuth();
  const location = useLocation();
  // The page's real path ('/admin/collections' behind /admin), not the router-relative one.
  const route = withBase(location.pathname);

  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [includeConsole, setIncludeConsole] = useState(true);
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [consoleErrors, setConsoleErrors] = useState<ConsoleEntry[]>([]);
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const capturingRef = useRef(false);

  // Debounced, aborted on every new keystroke, and silent on every failure.
  // Only runs while the modal is open - `active` gates it. No identity is
  // passed: the BFF front takes it from the session cookie.
  const { verdict, loading: verdictLoading } = useFeedbackVerdict({
    active: open,
    tool,
    feedback,
    route,
    csrfToken,
  });

  const captureNow = useCallback(async () => {
    if (capturingRef.current) return;
    capturingRef.current = true;
    setCapturingScreenshot(true);
    const blob = await captureScreenshot();
    capturingRef.current = false;
    setCapturingScreenshot(false);
    setScreenshotBlob(blob);
  }, []);

  // Modal open: snapshot the console buffer and session length once, and
  // kick off a screenshot capture if that toggle starts on.
  useEffect(() => {
    if (!open) return;
    setConsoleErrors(getConsoleErrors());
    setSessionSeconds(getSessionSeconds());
    if (includeScreenshot && !screenshotBlob) void captureNow();
    // Deliberately fires once per open, not on every dependency change -
    // ported from the source, same reasoning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (includeScreenshot) {
      if (!screenshotBlob && !capturingRef.current) void captureNow();
    } else if (screenshotBlob) {
      setScreenshotBlob(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeScreenshot]);

  useEffect(() => {
    if (!screenshotBlob || typeof URL.createObjectURL !== 'function') {
      setScreenshotUrl(null);
      return;
    }
    const url = URL.createObjectURL(screenshotBlob);
    setScreenshotUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshotBlob]);

  const handleOpen = useCallback(() => setOpen(true), []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setOpen(false);
  }, [submitting]);

  const handleSend = useCallback(async () => {
    const trimmed = feedback.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const result = await submitFeedback({
        tool,
        feedback: trimmed,
        route,
        sessionSeconds,
        identity: {
          // Advisory only: the BFF rebuilds user and tenant from the session.
          email: user?.email ?? '',
          userId: null,
          role: user?.role ?? null,
          tenantId: null,
          tenantName: null,
        },
        client: {
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          ua: navigator.userAgent,
          build: null,
        },
        consoleErrors,
        includeConsole,
        screenshot: screenshotBlob,
        includeScreenshot,
        csrfToken,
      });
      // Two whole sentences in the catalogue, not one sentence plus a
      // parenthetical bolted on (§4.1). `key` is the intake's issue id.
      toast.success(
        result.warning === 'screenshot_not_attached'
          ? MESSAGES.thanksNoScreenshot(result.key)
          : MESSAGES.thanks(result.key),
      );
      setFeedback('');
      setScreenshotBlob(null);
      setOpen(false);
    } catch (err) {
      // Never surface a raw server error, and keep the typed text in the
      // open modal so nothing is lost - not closing/clearing on this path
      // IS that requirement.
      // `FeedbackSubmitError` already carries a translated, safe message
      // (see `submit.ts`); anything else falls back to the same generic one.
      const message = err instanceof FeedbackSubmitError ? err.message : MESSAGES.errorGeneric;
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }, [feedback, submitting, tool, route, sessionSeconds, user, csrfToken, consoleErrors, includeConsole, screenshotBlob, includeScreenshot]);

  return (
    <>
      {variant === 'docked' ? (
        // The header spec's control #1: 32px, 8px radius, icon only. `h-800
        // w-800` because macondo's `sm` button is 36px and the spec is 32;
        // `rounded-200` because `ghost-square`'s inherited `rounded-button`
        // is 12px and the spec is 8. The accessible name is spelled out
        // ("Send feedback", not the pill's bare "Feedback") since there is
        // no visible label left to read.
        //
        // PA-23: wrapped in a Tooltip so the same string reaches sighted
        // users on hover/focus, not just assistive tech via aria-label.
        // Needs a TooltipProvider above it - `Topbar.tsx` supplies one for
        // this icon cluster. `asChild` merges the trigger's props onto the
        // IconButton itself rather than wrapping it in an extra element.
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              type="button"
              size="sm"
              variant="ghost-square"
              aria-label={MESSAGES.triggerLabel}
              onClick={handleOpen}
              className="h-800 w-800 rounded-200"
              icon={TRIGGER_ICON}
            />
          </TooltipTrigger>
          <TooltipContent>{MESSAGES.triggerLabel}</TooltipContent>
        </Tooltip>
      ) : (
      <Button
        type="button"
        variant="tertiary"
        onClick={handleOpen}
        // Arbitrary-value brackets, not `bottom-4`/`z-40`: importing macondo's
        // token CSS (see apps/web/src/styles/app.css) replaces Tailwind's
        // default z-index/position scales with macondo's own curated one
        // (confirmed by inspecting the compiled CSS: only z-5/10/20/50/100
        // exist, not z-40) - a named-scale utility for a value outside that
        // set silently compiles to NOTHING, not a fallback, which is exactly
        // how this button ended up rendered at (0,0) with no positioning at
        // all in a real browser. Below macondo's z-50 (Sheet/Modal), above
        // ordinary content.
        className="fixed bottom-[1.5rem] right-[1.5rem] z-[40] rounded-full shadow-md"
      >
        {TRIGGER_ICON}
        {MESSAGES.triggerLabel}
      </Button>
      )}

      <Modal
        open={open}
        onOpenChange={(next: boolean) => {
          if (!next) handleClose();
        }}
        title={MESSAGES.title}
        // `tool` is a section id (`links`, `cms`, `shell`) - a machine
        // identifier, so it interpolates rather than translating (§4.5).
        description={MESSAGES.about}
        confirmAction={{ label: MESSAGES.submit, onClick: handleSend, disabled: !feedback.trim() || submitting }}
        cancelAction={{ label: MESSAGES.cancel, variant: 'secondary', onClick: handleClose, disabled: submitting }}
      >
        <div className="space-y-4">
          <Textarea
            className="w-full"
            placeholder={MESSAGES.placeholder}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={5}
            maxLength={5000}
            autoFocus
          />

          <div className="border-border-primary text-callout text-typography-primary space-y-3 rounded-md border p-3">
            <div className="text-callout-medium">{MESSAGES.metaTitle}</div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-typography-secondary">{MESSAGES.screenshot}</span>
              {includeScreenshot ? (
                screenshotUrl ? (
                  <img src={screenshotUrl} alt="" className="border-border-primary h-10 w-16 rounded border object-cover" />
                ) : (
                  <span className="text-typography-tertiary">
                    {capturingScreenshot ? MESSAGES.screenshotCapturing : MESSAGES.screenshotUnavailable}
                  </span>
                )
              ) : (
                <span className="text-typography-tertiary">{MESSAGES.notIncluded}</span>
              )}
              <Toggle
                checked={includeScreenshot}
                onCheckedChange={setIncludeScreenshot}
                aria-label={MESSAGES.includeScreenshot}
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-typography-secondary">{MESSAGES.route}</span>
              <span className="truncate">{route}</span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-typography-secondary">{MESSAGES.signedInAs}</span>
              <span className="truncate">{user?.email ?? '-'}</span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-typography-secondary">{MESSAGES.sessionLength}</span>
              <span>{formatSessionLength(sessionSeconds)}</span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-typography-secondary">{MESSAGES.consoleErrors}</span>
              {includeConsole ? (
                // A count in a sentence: i18next picks the plural form (§4.2).
                <span>{MESSAGES.consoleCaptured(consoleErrors.length)}</span>
              ) : (
                <span className="text-typography-tertiary">{MESSAGES.notIncluded}</span>
              )}
              <Toggle
                checked={includeConsole}
                onCheckedChange={setIncludeConsole}
                aria-label={MESSAGES.includeConsole}
              />
            </div>
          </div>

          {/* The verdict, or the fact that one is being fetched, or nothing
              at all. Deliberately BELOW the context panel and ABOVE the
              privacy note - not adjacent to Send, which stays enabled in
              every state. `role="status"` + `aria-live="polite"` so it is
              announced when it arrives without interrupting typing; a
              polite region is right here precisely because the content is
              advisory. */}
          {verdict ? (
            <div
              className={`text-callout flex items-start gap-3 rounded-md p-3 ${VERDICT_TONES[verdict.verdict]}`}
              role="status"
              aria-live="polite"
            >
              <span className="text-caption-medium shrink-0 pt-[1px] uppercase tracking-wide">
                {MESSAGES[VERDICT_LABEL_KEYS[verdict.verdict]]}
              </span>
              <span className="min-w-0 flex-1">{verdict.sentence}</span>
            </div>
          ) : verdictLoading ? (
            <div
              className="text-callout text-typography-tertiary flex items-center gap-2 p-3 italic"
              role="status"
              aria-live="polite"
            >
              {/* Decoration: the sentence beside it already carries the
                  meaning, so it is hidden from assistive tech. */}
              <Spinner className="size-300 shrink-0" aria-hidden="true" />
              {MESSAGES.verdictChecking}
            </div>
          ) : null}

          <p className="text-caption text-typography-tertiary">{MESSAGES.privacy}</p>
        </div>
      </Modal>
    </>
  );
}
